// One page per thing (company, partner, person, file, charge, points month, payout, bill) and one page for any
// other row (/staff/record/<table>/<id>). Each shows its facts, every related thing as a table where each id is a
// link, everything that points at it, and one history.
// CHANGE-070: the same sections, tiles, jump links and actions in the staff app's kit. An id with no page reads as
// plain words, not an error.
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api, cents, points } from '../../lib'
import { Button, Busy, Err, Fresh, Page, Sources, Table } from './kit'
import { FactList, HistoryList, LinkedList, RowsTable, SectionBlock, type Fact, type HistoryItem, type Linked, type Row, type Section } from './eye'
import { ViewAsButton } from './ViewAs'
import { LogoEdit } from '../../app/logo'

type Thing = {
  kind: string; id: string; title: string; table?: string; view_as?: 'company' | 'partner'
  facts?: Fact[]; row?: Row; totals?: Record<string, number | Record<string, number>>
  file?: { type: 'batch' | 'receipt'; name: string; has_blob: boolean }
  sections: Section[]; linked: Linked[]; history: HistoryItem[]
  can_recheck?: boolean; rejected_receipts?: number
  logo_url?: string | null
}

const KINDS: Record<string, string> = {
  company: 'Company', partner: 'Partner', person: 'Person', file: 'File', charge: 'Charge', calc: 'Points for a month',
  payout: 'Payout', bill: 'Bill',
}

export function ThingPage() {
  const { kind = '', id = '', table } = useParams()
  const isRecord = !!table
  const path = isRecord ? `/api/staff/records/${table}/${id}` : `/api/staff/things/${kind}/${id}`
  const q = useQuery({ queryKey: ['staff', 'thing', path], queryFn: () => api<Thing>('GET', path), enabled: isRecord || kind in KINDS })
  const [rechecked, setRechecked] = useState<{ path: string; r: RecheckAll | Error } | null>(null)
  const qc = useQueryClient()
  useEffect(() => { if (q.data) document.title = `${q.data.title} | ernd staff` }, [q.data])
  if (!isRecord && !(kind in KINDS)) return <Page title="No such page"><p className="st-note">There is no staff page for {kind}.</p></Page>
  if (q.isLoading) return <Busy />
  if (q.error) return <Page title="Not found"><Err error={q.error} /><p className="st-note st-mono">{id}</p></Page>
  const t = q.data!
  // a file row links to its charge; the charge's raw row links back here (#row-N)
  const rowAnchor = t.kind === 'file' ? (r: Row) => (typeof r.row_no === 'number' ? `row-${r.row_no}` : undefined) : undefined
  return (
    <Page testid={`staff-thing-${t.kind}`}
      title={<span data-testid="staff-thing-title">{t.title}</span>}
      sub={<Sources>
        <span className="st-fresh">{isRecord ? t.table : KINDS[t.kind]}</span>
        <span className="st-fresh st-mono" data-testid="staff-thing-id">{t.id}</span>
        <Fresh at={q.dataUpdatedAt ? new Date(q.dataUpdatedAt) : null} live />
      </Sources>}
      right={<>
        {t.view_as ? <ViewAsButton kind={t.view_as} id={t.id} /> : null}
        {(t.can_recheck || (t.kind === 'company' && !!t.rejected_receipts)) ? <RecheckButton t={t} queryKey={['staff', 'thing', path]} done={(r) => setRechecked({ path, r })} /> : null}
        {t.file?.has_blob ? (
          <>
            <a className="st-btn primary" href={`/api/staff/files/${t.id}`} target="_blank" rel="noreferrer" data-testid="staff-file-open">Open file</a>
            <a className="st-btn" href={`/api/staff/files/${t.id}?download=1`} data-testid="staff-file-download">Download</a>
          </>
        ) : null}
      </>}>
      {rechecked?.path === path ? <RecheckResult r={rechecked.r} /> : null}
      {!isRecord && (t.kind === 'company' || t.kind === 'partner') ? (
        // CHANGE-042: staff keep setting logos for people; each change is in the audit log as staff
        <div className="st-eye-logo">
          <LogoEdit name={t.title} url={t.logo_url} path={`/api/staff/${t.kind === 'company' ? 'companies' : 'partners'}/${t.id}/logo`}
            canEdit onChange={() => qc.invalidateQueries({ queryKey: ['staff', 'thing', path] })} testid="staff-logo" />
        </div>
      ) : null}
      {t.totals ? <Totals kind={t.kind} totals={t.totals} /> : null}
      {t.facts ? <FactList facts={t.facts} /> : null}
      {t.row ? <RowsTable rows={[t.row]} testid="staff-record-row" /> : null}
      <nav className="st-strip st-eye-jump" aria-label="On this page">
        {t.sections.filter((s) => s.rows.length).map((s) => <a key={s.key ?? s.title} href={`#${s.key ?? s.title}`}>{s.title}<span>{s.rows.length}</span></a>)}
        {t.linked.length > 0 ? <a href="#linked">Everything linked</a> : null}
        <a href="#history">History</a>
      </nav>
      {t.sections.map((s) => <SectionBlock key={s.key ?? s.title} s={s} anchor={s.key === 'rows' ? rowAnchor : undefined} />)}
      <LinkedList linked={t.linked} />
      <HistoryList items={t.history} />
    </Page>
  )
}

function Totals({ kind, totals }: { kind: string; totals: Thing['totals'] }) {
  const n = (k: string) => Number((totals as Record<string, number>)[k] ?? 0)
  const tiles: { label: string; value: string; href?: string; testid: string }[] = kind === 'company' ? [
    { label: 'Ready to pay out', value: points(n('ready')), href: '#points_by_month', testid: 'ready' },
    { label: 'Spendable', value: points(n('spendable')), href: '#points_by_month', testid: 'spendable' },
    { label: 'Waiting for the partner to pay', value: points(n('pending')), href: '#points_by_month', testid: 'pending' },
    { label: 'Held for a payout', value: points(n('reserved')), href: '#payouts', testid: 'reserved' },
    { label: 'Earned, all time', value: points(n('earned')), href: '#points_by_month', testid: 'earned' },
    { label: 'Paid out', value: cents(n('paid_out_cents')), href: '#payouts', testid: 'paid_out' },
  ] : [
    { label: 'Owes us now', value: cents(n('receivable_cents')), href: '#bills', testid: 'receivable' },
    { label: 'We owe them, credit', value: cents(n('credit_owed_cents')), href: '#bills', testid: 'credit_owed' },
    { label: 'Billed, all time', value: cents(n('billed_cents')), href: '#bills', testid: 'billed' },
    { label: 'Bills paid', value: cents(n('bills_paid_cents')), href: '#bills', testid: 'bills_paid' },
    { label: 'Points waiting on a bill', value: points(n('points_waiting')), href: '#bills', testid: 'points_waiting' },
  ]
  return (
    <div className="st-eye-tiles" data-testid="staff-totals">
      {tiles.map((t) => (
        <a key={t.label} className="st-eye-tile" href={t.href} data-testid={`staff-total-${t.testid}`}>
          <span className="st-muted">{t.label}</span>
          <strong>{t.value}</strong>
        </a>
      ))}
    </div>
  )
}

// Fixed ids the app writes audit rows against (pr/core/flags.py). They have no row of their own to open.
const KNOWN_IDS: Record<string, string> = {
  '0190a000-0000-7000-8000-00000000f1a6': 'The payouts pause switch',
  '0190a000-0000-7000-8000-00000000f1a7': 'The card requests switch',
}

/** /staff/go/<id>: find what the id is and open its page. An id with no page says so in words. */
export function GoPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const q = useQuery({ queryKey: ['staff', 'go', id], queryFn: () => api<{ path: string }>('GET', `/api/staff/go/${id}`), retry: false })
  useEffect(() => { if (q.data) navigate(q.data.path, { replace: true }) }, [q.data, navigate])
  if (q.error) {
    const known = KNOWN_IDS[id.toLowerCase()]
    return (
      <Page title={known ?? 'No page for this id'} testid="staff-go-none">
        <p className="st-note">
          {known ? 'It has no page of its own. Its changes are in the audit log.' : 'Nothing in the app has a page for this id. It may be a switch, a template or a deleted row.'}
        </p>
        <p className="st-note st-mono">{id}</p>
        <p className="st-row">
          <Link className="st-link" to={`/staff/log?object_id=${encodeURIComponent(id)}`}>Audit log for this id</Link>
          <Link className="st-link" to="/staff">Today</Link>
        </p>
      </Page>
    )
  }
  return <Busy what="Finding it" />
}

// CHANGE-030: read rejected receipts again under today's rules. One receipt, or every rejected one of a company.
type Recheck = { id: string; invoice_number: string | null; old_status: string; new_status: string; new_reason: string | null; moved: boolean }
type RecheckAll = { checked: number; moved: Record<string, number>; rows: Recheck[] }
const STATUS: Record<string, string> = { counted: 'counted', needs_review: 'to review', duplicate: 'duplicate', rejected: 'rejected' }

function RecheckButton({ t, queryKey, done }: { t: Thing; queryKey: string[]; done: (r: RecheckAll | Error) => void }) {
  const qc = useQueryClient()
  const all = t.kind === 'company'
  const m = useMutation({
    mutationFn: () => all
      ? api<RecheckAll>('POST', `/api/staff/companies/${t.id}/invoices/recheck`)
      : api<Recheck>('POST', `/api/staff/invoices/${t.id}/recheck`).then((r) => ({ checked: 1, moved: r.moved ? { [r.new_status]: 1 } : {}, rows: [r] })),
    onSuccess: (r) => { done(r); qc.invalidateQueries({ queryKey }) },
    onError: (e) => done(e),
  })
  return (
    <Button disabled={m.isPending} onClick={() => m.mutate()} data-testid={all ? 'staff-recheck-all' : 'staff-recheck-one'}>
      {m.isPending ? 'Checking' : all ? `Check all rejected (${t.rejected_receipts})` : 'Check again'}
    </Button>
  )
}

function RecheckResult({ r }: { r: RecheckAll | Error }) {
  if (r instanceof Error) return <Err error={r} />
  const moved = Object.values(r.moved).reduce((a, b) => a + b, 0)
  const parts = Object.entries(r.moved).map(([s, n]) => `${n} ${STATUS[s] ?? s}`).join(', ')
  return (
    <div className="st-stack" data-testid="staff-recheck-result">
      <p className="st-banner" data-testid="staff-recheck-summary">
        {r.checked === 0 ? 'Nothing to check.'
          : r.checked === 1 ? (moved ? `Checked again. It is now ${STATUS[r.rows[0].new_status] ?? r.rows[0].new_status}.` : 'Checked again. It stays rejected.')
          : moved === 0 ? `Checked ${r.checked}. None moved; all stay rejected.`
          : `Checked ${r.checked}. ${moved} moved: ${parts}.${r.checked > moved ? ` ${r.checked - moved} stay rejected.` : ''}`}
      </p>
      {r.rows.length > 0 ? (
        <Table rows={r.rows} rowKey={(x) => x.id} rowAttrs={() => ({ 'data-testid': 'staff-recheck-row' })} columns={[
          { key: 'r', head: 'Receipt', primary: true, cell: (x) => <Link className="st-link" to={`/staff/file/${x.id}`}>{x.invoice_number || x.id}</Link> },
          { key: 'was', head: 'Was', cell: (x) => STATUS[x.old_status] ?? x.old_status },
          { key: 'now', head: 'Now', cell: (x) => <span className="st-strong">{STATUS[x.new_status] ?? x.new_status}</span> },
          { key: 'why', head: 'Why', cell: (x) => x.new_reason },
        ]} />
      ) : null}
    </div>
  )
}
